import { useState, useEffect } from "react";

import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";

import { NavLink } from "react-router-dom";

//Componentes
import SpinnerLoading from "@/frontend/componentes/Spinner";

//Common
import {
  fn_getFormaRateio,
  fn_getTipoRateio,
  fn_getTipoValorPagamento,
} from "@/common/siig";

//Util
import { ICONES_BOTAO } from "@/frontend/utils/icones";
import { fn_agruparRateioContaCentro } from "@/frontend/utils/siig/rateio";

//API
import { api_listar, ROTA_LISTAR } from "@/frontend/services/api/api_list";

//Telas
import TRateio_det_aba_valor from "./rateio_det_aba_valor";
import { TIPO_PAGAMENTO, TIPO_RATEIO } from "@/common/constantes";
import { MENSAGENS_ERRO, showMensagemErro } from "@/frontend/utils/mensagens";

function TRateio_det({
  modalOpen,
  toggleModal,
  solicitacao,
  pagamento,
  isParcelado,
}) {
  //### USE STATE ###
  const [activeTab, setActiveTab] = useState("1");

  const [rateios, setRateios] = useState([]);
  const [tiposValor, setTiposValor] = useState([]);

  const [tipoRateio, setTipoRateio] = useState(null);
  const [formaRateio, setFormaRateio] = useState(null);

  const [loading, setLoading] = useState(true);

  const [codigosEmpresa, setCodigosEmpresa] = useState({}); //código de cada empresa, pelo id

  //### USE EFFECT ###
  //Carrega o código das empresas para a coluna Empresa (também as inativas, pois o centro pode ser de uma empresa inativa)
  useEffect(() => {
    async function loadCodigosEmpresa() {
      const empresas = await api_listar(ROTA_LISTAR.EMPRESA);

      setCodigosEmpresa(
        Object.fromEntries(
          (empresas || []).map((empresa) => [empresa.id, empresa.codigo]),
        ),
      );
    }

    loadCodigosEmpresa();
  }, []);

  //Inicialização da tela
  useEffect(() => {
    //Carrega os dados do rateio de todos os pagamentos
    async function loadRateioPagamentos() {
      try {
        let pagamentos = [];

        //Se for consulta do pagamento e enviar o pagamento completo
        if (pagamento) {
          pagamentos = [pagamento];
        }
        //Se for consulta do rateio de todos os pagamentos da solicitação
        else {
          if (solicitacao.pagamentos) {
            pagamentos = solicitacao.pagamentos;
          } else {
            pagamentos = await api_listar(
              ROTA_LISTAR.PAGAMENTOS_SOLICITACAO,
              `id_solicitacao=${solicitacao.id}&include_completo=S`,
            );
          }

          //Obtém o tipo e a forma de rateio quando forem iguais
          //em todos os pagamentos
          if (pagamentos.length > 0) {
            const tipoRateio = pagamentos[0].tipo_rateio;
            const formaRateio = pagamentos[0].forma_rateio;

            const tipoRateioIgual = pagamentos.every(
              (pag) => pag.tipo_rateio == tipoRateio,
            );

            const formaRateioIgual = pagamentos.every(
              (pag) => pag.forma_rateio == formaRateio,
            );
            const isRecorrente =
              solicitacao.tipo_pagamento == TIPO_PAGAMENTO.RECORRENTE;

            if (tipoRateioIgual || !isRecorrente) {
              setTipoRateio(tipoRateio);
            }

            if (formaRateioIgual || !isRecorrente) {
              setFormaRateio(formaRateio);
            }
          }
        }

        //Obtém os rateios dos pagamentos
        const rateios = pagamentos.flatMap((pag) => pag.rateio);
        const rateiosAgrup = fn_agruparRateioContaCentro(rateios);
        setRateios(rateiosAgrup);
      } catch {
        showMensagemErro(MENSAGENS_ERRO.TENTATIVA_OPERACAO, "carregar rateio");
      } finally {
        setLoading(false);
      }
    }

    loadRateioPagamentos();
  }, [solicitacao, pagamento]);

  //Ao receber a informação dos rateios
  useEffect(() => {
    const tiposValor = [
      ...new Set(
        rateios
          .filter((rateio) => rateio.tipo_valor_conta !== null)
          .map((rateio) => rateio.tipo_valor_conta),
      ),
    ].sort((a, b) => a.localeCompare(b));

    setTiposValor(tiposValor);
  }, [rateios]);

  //### FUNÇÕES ###
  //Retorna o componente NavLink montado de acordo com o tipo valor
  function getNavLink(codTab, tipoValor) {
    return (
      <NavLink
        className={activeTab == String(codTab) ? "tabAtivaNome" : "tabNome"}
        onClick={() => setActiveTab(codTab)}
      >
        {fn_getTipoValorPagamento(tipoValor)?.label}
      </NavLink>
    );
  }

  return (
    <Modal isOpen={modalOpen} backdrop="static" size="lg">
      <ModalHeader toggle={toggleModal} className="titulo-tela-modal">
        {pagamento ? "Rateio Pagamento" : "Rateio Solicitação"}
      </ModalHeader>

      <ModalBody>
        {solicitacao && (
          <div className="d-flex align-items-center mb-2">
            <div className="me-4">
              <span className="fw-bold me-2">Solicitação:</span>
              {solicitacao.numero}
            </div>
            <div className="ms-4">
              <span className="fw-bold me-2">Qtde pagamento:</span>
              {solicitacao.qtde_pagamentos}
            </div>

            {tipoRateio && (
              <>
                <div className="ms-4">
                  <span className="fw-bold me-2"> Tipo rateio:</span>
                  {fn_getTipoRateio(tipoRateio)?.label}
                </div>

                {formaRateio && (
                  <div className="ms-4">
                    <span className="fw-bold me-2"> Forma rateio:</span>
                    {fn_getFormaRateio(tipoRateio, formaRateio)?.label}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {pagamento && (
          <Row className="mb-2">
            <Col>
              <div className="d-flex align-items-center mb-2">
                {!isParcelado && (
                  <div>
                    <span className="fw-bold me-2"> Pagamento:</span>
                    {pagamento.num_pag} / {pagamento.qtde_pagamento}
                  </div>
                )}

                <div className="ms-4">
                  <span className="fw-bold me-2"> Tipo rateio:</span>
                  {fn_getTipoRateio(pagamento.tipo_rateio)?.label}
                </div>

                <div className="ms-4">
                  <span className="fw-bold me-2"> Forma rateio:</span>
                  {
                    fn_getFormaRateio(
                      pagamento.tipo_rateio,
                      pagamento.forma_rateio,
                    )?.label
                  }
                </div>

                {/* Posição gravada do rateio automático (no parcelado, fica na parcela 1, que tem o rateio) */}
                {pagamento.tipo_rateio == TIPO_RATEIO.AUTOMATICO &&
                  (!isParcelado ||
                    pagamento.num_pag == 1 ||
                    pagamento.posicao_rateio) && (
                    <div className="ms-4">
                      <span className="fw-bold me-2"> Posição:</span>
                      {pagamento.posicao_rateio || "Não gravada"}
                    </div>
                  )}
              </div>
            </Col>
          </Row>
        )}

        <div className="scroll-modal-fixed">
          {(loading && <SpinnerLoading />) || (
            <div className="m-1">
              <Nav tabs>
                <NavItem className="tabBorda">
                  <NavLink
                    className={activeTab == "1" ? "tabAtivaNome" : "tabNome"}
                    onClick={() => setActiveTab("1")}
                  >
                    Valor documento
                  </NavLink>
                </NavItem>

                {tiposValor.map((tipo, index) => (
                  <NavItem key={index} className="tabBorda">
                    {getNavLink(String(index + 2), tipo)}
                  </NavItem>
                ))}
              </Nav>

              {/* CONTEÚDO DAS ABAS */}
              <TabContent activeTab={activeTab}>
                {/* ABA VALOR DOCUMENTO */}
                <TabPane tabId="1">
                  {activeTab === "1" && (
                    <TRateio_det_aba_valor
                      tipo_rateio={pagamento?.tipo_rateio}
                      forma_rateio={pagamento?.forma_rateio}
                      codigosEmpresa={codigosEmpresa}
                      rateios={rateios.filter(
                        (rateio) => rateio.tipo_valor_conta === null,
                      )}
                    />
                  )}
                </TabPane>

                {/* ABAS TIPO VALOR */}
                {tiposValor.map((tipo, index) =>
                  activeTab === String(index + 2) ? (
                    <TRateio_det_aba_valor
                      key={index}
                      tipo_rateio={pagamento?.tipo_rateio}
                      forma_rateio={pagamento?.forma_rateio}
                      codigosEmpresa={codigosEmpresa}
                      rateios={rateios.filter(
                        (r) => r.tipo_valor_conta === tipo,
                      )}
                    />
                  ) : null,
                )}
              </TabContent>
            </div>
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <Button name="btnFechar" color="secondary" onClick={toggleModal}>
          {ICONES_BOTAO.FECHAR} Fechar
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default TRateio_det;
